import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import http from '@/libs/http'

export interface IProject {
	_id: string
	state: string
	name: string
	projectNumber: string
	blobImage: string
	comment: string
	isArchived: boolean
	modified: number
	shortName: string
}

export const useProjectStore = defineStore('projects', () => {
	// --- STATE ---
	const projects = ref<IProject[]>([])
	const currentProject = ref<IProject | null>(null)
	const loading = ref<Record<string, boolean>>({})

	// --- HELPERS ---
	function setLoading(key: string, val: boolean) {
		loading.value[key] = val
	}

	function formatError(error: any): string {
		return (
			error?.response?.data?.msg ||
			error?.response?.data ||
			error.message ||
			'An unexpected projcet store error occurred'
		)
	}

	async function withLoading<T>(key: string, fn: () => Promise<T>): Promise<T> {
		if (loading.value[key]) {
			throw new Error(`${key} is already in progress`)
		}
		try {
			setLoading(key, true)
			return await fn()
		} finally {
			setLoading(key, false)
		}
	}

	// --- GETTERS ---

	const projectMap = computed(() => {
		const map: Record<string, IProject> = {}
		for (const project of projects.value) {
			map[project._id] = project
		}
		return map
	})

	// --- ACTIONS ---
	async function listProjects() {
		return withLoading('listProjects', async () => {
			try {
				const resp = await http.getAuth(`/api/v2/projects`)
				projects.value = resp || []
				return resp || []
			} catch (error: any) {
				console.error(error)
				throw new Error(formatError(error))
			}
		})
	}

	async function getProject(id: string) {
		if (!id) throw new Error('Project ID is required')
		return withLoading(`getProject-${id}`, async () => {
			try {
				const resp = await http.getAuth(`/api/v2/project/${id}`)
				currentProject.value = resp || null
				return resp || null
			} catch (error: any) {
				console.error(error)
				throw new Error(formatError(error))
			}
		})
	}

	async function addNewProject(data: Omit<IProject, '_id' | 'modified'>) {
		return withLoading('addNewProject', async () => {
			try {
				const newProj = await http.postAuth(`/api/v2/project`, data)
				projects.value.unshift(newProj)
				return newProj
			} catch (error: any) {
				console.error(error)
				throw new Error(formatError(error))
			}
		})
	}

	async function updateProject(data: IProject) {
		return withLoading('updateProject', async () => {
			try {
				const updated = await http.putAuth(`/api/v2/project/${data._id}`, data)
				await getProject(data._id)
				return updated
			} catch (error: any) {
				console.error(error)
				throw new Error(formatError(error))
			}
		})
	}

	async function deleteProject(_id: string) {
		return withLoading(`deleteProject-${_id}`, async () => {
			try {
				await http.deleteAuth<void>(`/api/v2/project/${_id}`)
				return true
			} catch (error: any) {
				console.error(error)
				throw new Error(formatError(error))
			}
		})
	}

	// --- INITIAL LOAD ---
	const _init = async () => {
		await listProjects()
	}
	_init()

	return {
		// state
		projects,
		currentProject,
		loading,
		//getters
		projectMap,

		// actions
		listProjects,
		getProject,
		addNewProject,
		updateProject,
		deleteProject,
	}
})
